import { Center, Spinner, Box, Button, Stack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { UserAppEvents } from '@duke-hq/ui/components/UserAppEvents/UserAppEvents';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { StudentType } from './StudentDetailPage';
import { Download } from 'lucide-react';

export function StudentAuditLogs({ student }: { student: StudentType }) {
  const {
    data: studentActivityItems,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = trpcAdmin.students.studentActivity.useInfiniteQuery(
    {
      userId: student.id,
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      enabled: !!student.id,
    },
  );

  const { isFetching: isExporting, refetch: fetchCSV } =
    trpcAdmin.students.exportStudentActivityToCSV.useQuery(
      { userId: student.id },
      { enabled: false },
    );

  const handleDownload = async () => {
    const { data } = await fetchCSV();
    if (!data) {
      return;
    }

    const blob = new Blob([data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const now = new Date();
    const timestamp = now.toISOString().replace(/[:T]/g, '-').split('.')[0];

    const a = document.createElement('a');
    a.href = url;
    a.download = `student-${student.id}-audit-logs-${timestamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
  };

  if (isFetching && !studentActivityItems) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  const activityItemsArray = studentActivityItems?.pages?.flatMap((page) => page.items) ?? [];

  if (activityItemsArray.length === 0) {
    return (
      <Center py={8}>
        <Trans>No activity found</Trans>
      </Center>
    );
  }

  return (
    <>
      <Box p={4}>
        <UserAppEvents studentActivityItems={activityItemsArray} />
      </Box>
      <Box
        position="sticky"
        bottom="0"
        bg="bg.surface"
        borderTopWidth="1px"
        borderColor="gray.100"
        py={4}
        px={4}
        zIndex={1}
      >
        <Center>
          <Stack direction="row" spacing={4}>
            {hasNextPage ? (
              <Button onClick={() => fetchNextPage()} isLoading={isFetching}>
                <Trans>Load more</Trans>
              </Button>
            ) : null}
            <Button
              onClick={handleDownload}
              isLoading={isExporting}
              colorScheme="blue"
              variant="outline"
              leftIcon={<Download size="20px" />}
            >
              <Trans>Download Audit Logs</Trans>
            </Button>
          </Stack>
        </Center>
      </Box>
    </>
  );
}
